import queryString from 'query-string';
import CryptoJS from 'crypto-js';

import { config } from '../../config';
import { AuthError } from '../../api/errors';
import { Sentry } from '../sentry';

import {
  DPOP_NONCE_SCOPE,
  buildDpopProofHeader,
  isDpopNonceRequired,
  isDpopProofRejected,
  rememberDpopNonce,
  reportDpopNonceChallenge,
  reportDpopProofRejected,
} from './dpop';
import {
  fetchJsonWithRetry,
  generateCodeChallenge,
  generateRandomAuthStateString,
  getDeepLink,
} from './utilities';

/**
 * Client for the standards-based OpenID Connect provider the API exposes at
 * `/oidc`, next to the legacy `/auth/*` endpoints. The access tokens it issues
 * are the same JWTs as before, so only how tokens are obtained, refreshed and
 * revoked lives here. Both apps are public clients (`token_endpoint_auth_method
 * = none`): no client secret, no Basic header, PKCE (S256) everywhere it
 * applies.
 *
 * The legacy client in ./oauth.js is untouched — which one a login uses is
 * decided by the remote `authentication.useOidc` configuration flag, and which
 * one a *stored session* belongs to is recorded by the token provider marker
 * (see ../tokens.js).
 */

// `api-clients:manage` backs "API access" on the web profile page. Mobile has
// no UI for it yet, but asks for the same scopes.
export const OIDC_SCOPE =
  'openid offline_access customer employee lead api-clients:manage';

export const OIDC_LOGIN_ASSERTION_TOKEN_TYPE =
  'urn:formue:params:oauth:token-type:login-assertion';
export const OIDC_ID_TOKEN_TYPE = 'urn:ietf:params:oauth:token-type:id_token';
const TOKEN_EXCHANGE_GRANT = 'urn:ietf:params:oauth:grant-type:token-exchange';

const hashValue = (value) =>
  value ? CryptoJS.SHA256(String(value)).toString() : undefined;

const getOidcBaseUrl = () => `${config.api.host}/oidc`;

/**
 * Endpoint paths used until the discovery document has been read, or when it
 * could not be read at all. They match what the provider publishes.
 */
export const getDefaultOidcEndpoints = () => {
  const base = getOidcBaseUrl();
  return {
    issuer: base,
    authorization_endpoint: `${base}/auth`,
    token_endpoint: `${base}/token`,
    revocation_endpoint: `${base}/token/revocation`,
  };
};

let cachedConfiguration = null;
let configurationRequest = null;

/**
 * Read `GET /oidc/.well-known/openid-configuration` once and cache it for the
 * lifetime of the app. Safe to call repeatedly — concurrent callers share one
 * request, and a failed read is not cached, so the next call tries again.
 * Never throws: on failure the default endpoints are returned so a login is
 * never blocked on discovery.
 */
export const loadOidcConfiguration = async () => {
  if (cachedConfiguration) {
    return cachedConfiguration;
  }

  if (!configurationRequest) {
    const url = `${getOidcBaseUrl()}/.well-known/openid-configuration`;
    configurationRequest = (async () => {
      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) {
          throw new Error(
            `OIDC discovery failed with status ${response.status}`
          );
        }
        const json = await response.json();
        cachedConfiguration = { ...getDefaultOidcEndpoints(), ...json };
        return cachedConfiguration;
      } catch (error) {
        Sentry.addBreadcrumb({
          message: 'OIDC discovery document could not be read',
          category: 'auth',
          level: 'warning',
          data: { url, error_message: error?.message },
        });
        return getDefaultOidcEndpoints();
      } finally {
        configurationRequest = null;
      }
    })();
  }

  return configurationRequest;
};

/**
 * Synchronous view of the cached discovery document, falling back to the
 * default paths. Used where a URL has to be built during render.
 */
export const getOidcEndpoints = () =>
  cachedConfiguration || getDefaultOidcEndpoints();

// Test hook
export const resetOidcConfigurationCache = () => {
  cachedConfiguration = null;
  configurationRequest = null;
};

/**
 * Build the `/oidc/auth` URL for the browser-based logins (BankID, Swedish
 * BankID, MitID). Same signature as the legacy getAuthorizationUrl so the two
 * are interchangeable at the call sites; `method`, `ui_locales` and
 * `login_hint` keep their legacy meaning.
 */
export const getOidcAuthorizationUrl = (
  method,
  callbackUrl,
  codeVerifier,
  options = {}
) => {
  const query = {
    client_id: config.api.client.user,
    response_type: 'code',
    redirect_uri: getDeepLink(callbackUrl),
    scope: OIDC_SCOPE,
    state: generateRandomAuthStateString(),
    code_challenge: generateCodeChallenge(codeVerifier),
    code_challenge_method: 'S256',
  };

  if (method) {
    query.method = method;
  }

  const { locale, platform } = options;

  if (locale) {
    query.ui_locales = locale;
  }

  if (platform) {
    query.login_hint = platform === 'ios' ? 'platform-IOS' : 'platform-ANDROID';
  }

  const { authorization_endpoint: endpoint } = getOidcEndpoints();
  return `${endpoint}?${queryString.stringify(query)}`;
};

const toOidcError = (params, error) => {
  const body = error?.response;
  if (body && typeof body === 'object' && body.error) {
    const oidcError = new AuthError(body.error_description || body.error);
    oidcError.code = body.error;
    oidcError.description = body.error_description;
    oidcError.status = error.status;
    oidcError.grantType = params.grant_type;
    return oidcError;
  }
  return error;
};

const formPostOptions = (params, extraHeaders = {}) => ({
  method: 'POST',
  body: queryString.stringify(params),
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/x-www-form-urlencoded',
    ...extraHeaders,
  },
});

/**
 * POST form-encoded parameters to an `/oidc` endpoint with a DPoP proof per
 * attempt (when a key exists — a retry never replays a `jti`), answering one
 * nonce challenge on the way (RFC 9449 §8): a provider that wants a nonce
 * rejects the proof as `use_dpop_nonce` *before* it looks at the grant and
 * names the nonce in `DPoP-Nonce`, and the same request is then posted once
 * more with that nonce in the proof. The grant was never processed, so this
 * is safe even for a refresh token — which must otherwise never be re-sent.
 * Once only, so a provider that keeps rejecting our nonce cannot loop us; and
 * only when a proof was sent and the challenge named a nonce, so nothing else
 * is ever retried here. Every nonce the provider hands out, on any response,
 * is remembered for the next POST.
 *
 * `onProof` sees each proof as it is sent (for the rejection telemetry).
 */
const postSignedOidcForm = async (url, params, { attempts, onProof } = {}) => {
  let proofSent = false;
  let challengeNonce = null;

  const send = () =>
    fetchJsonWithRetry(
      url,
      async () => {
        const dpop = await buildDpopProofHeader({ method: 'POST', url });
        proofSent = Boolean(dpop.DPoP);
        if (onProof) {
          onProof(dpop.DPoP);
        }
        return formPostOptions(params, dpop);
      },
      attempts,
      {
        onResponse: (response) => {
          challengeNonce = rememberDpopNonce(
            DPOP_NONCE_SCOPE.PROVIDER,
            response.headers
          );
        },
      }
    );

  try {
    return await send();
  } catch (error) {
    if (!proofSent || !challengeNonce || !isDpopNonceRequired(error)) {
      throw error;
    }
    reportDpopNonceChallenge({ endpointKind: 'token', htu: url });
    return send();
  }
};

/**
 * POST a grant to the token endpoint. Standard OAuth error bodies
 * (`{ error, error_description }`, HTTP 400) become an AuthError with `.code`;
 * anything else (network failure, 5xx, unparsable body) is rethrown as-is.
 * Secrets are only ever hashed or reported as booleans to Sentry.
 */
const postOidcForm = async (url, params, { attempts } = {}) => {
  let lastProof;

  try {
    return await postSignedOidcForm(url, params, {
      attempts,
      onProof: (proof) => {
        lastProof = proof;
      },
    });
  } catch (error) {
    const oidcError = toOidcError(params, error);
    if (isDpopProofRejected(oidcError)) {
      reportDpopProofRejected({
        endpointKind: 'token',
        htu: url,
        proof: lastProof,
      });
    }
    Sentry.captureException(oidcError, {
      level: 'error',
      extra: {
        token_url: url,
        request_grant_type: params?.grant_type,
        request_redirect_uri: params?.redirect_uri,
        request_client_id: params?.client_id,
        request_has_code: Boolean(params?.code),
        request_has_code_verifier: Boolean(params?.code_verifier),
        request_code_hash: hashValue(params?.code),
        request_code_verifier_hash: hashValue(params?.code_verifier),
        request_has_refresh_token: Boolean(params?.refresh_token),
        request_has_subject_token: Boolean(params?.subject_token),
        request_subject_token_type: params?.subject_token_type,
        response_status: error?.status,
        response_error: oidcError?.code,
        response_error_description: oidcError?.description,
        response_headers: error?.headers,
      },
      tags: {
        internal_system: 'authentication',
        token_provider: 'oidc',
        token_exchange: params?.grant_type,
      },
    });
    throw oidcError;
  }
};

const normalizeTokenResponse = (result, grantType) => {
  if (!result?.access_token) {
    const error = new AuthError(
      'Token response did not contain an access token'
    );
    error.grantType = grantType;
    throw error;
  }

  Sentry.addBreadcrumb({
    message: 'OIDC token issued',
    category: 'auth',
    level: 'info',
    data: {
      grant_type: grantType,
      scope: result.scope,
      expires_in: result.expires_in,
      token_type: result.token_type,
      has_refresh_token: Boolean(result.refresh_token),
    },
  });

  return {
    accessToken: result.access_token,
    refreshToken: result.refresh_token,
    expiresIn: result.expires_in,
    tokenType: result.token_type,
    scope: result.scope,
    issuedTokenType: result.issued_token_type,
  };
};

const postTokenRequest = async (params, options) => {
  const { token_endpoint: tokenUrl } = await loadOidcConfiguration();
  const result = await postOidcForm(tokenUrl, params, options);
  return normalizeTokenResponse(result, params.grant_type);
};

/**
 * Browser login: exchange the authorization code from the redirect back for
 * tokens. `callbackUrl` must resolve to the same redirect_uri the
 * authorization request carried.
 */
export const exchangeOidcAuthCodeForToken = async (
  code,
  codeVerifier,
  callbackUrl
) =>
  postTokenRequest({
    grant_type: 'authorization_code',
    client_id: config.api.client.user,
    code,
    redirect_uri: getDeepLink(callbackUrl),
    code_verifier: codeVerifier,
  });

/**
 * Headless logins (MobileID biometrics, passkeys, headless Signicat sessions):
 * the `/auth/*` helper endpoints hand back a 60-second single-use login
 * assertion which is exchanged here. Only the headless Signicat assertion is
 * bound to a PKCE challenge, so only that exchange passes a `codeVerifier`.
 */
export const exchangeOidcLoginAssertionForToken = async (
  assertion,
  { codeVerifier } = {}
) => {
  const params = {
    grant_type: TOKEN_EXCHANGE_GRANT,
    client_id: config.api.client.user,
    subject_token: assertion,
    subject_token_type: OIDC_LOGIN_ASSERTION_TOKEN_TYPE,
  };

  if (codeVerifier) {
    params.code_verifier = codeVerifier;
  }

  return postTokenRequest(params);
};

/**
 * Employee SSO (web): exchange an Entra id token. Each id token can be
 * exchanged exactly once on `/oidc`; after that live on the refresh token.
 */
export const exchangeOidcIdTokenForToken = async ({ idToken }) =>
  postTokenRequest({
    grant_type: TOKEN_EXCHANGE_GRANT,
    client_id: config.api.client.user,
    subject_token: idToken,
    subject_token_type: OIDC_ID_TOKEN_TYPE,
    subject_issuer: 'azure-ad',
  });

/**
 * Refresh. Refresh tokens always rotate, and reusing an old one revokes the
 * whole login — so this request is never retried: after a network error we
 * cannot know whether the server rotated the token, and a replay could kill
 * the session. Callers serialise refreshes through ./session.js.
 */
export const exchangeOidcRefreshTokenForToken = async ({
  token: refreshToken,
}) =>
  postTokenRequest(
    {
      grant_type: 'refresh_token',
      client_id: config.api.client.user,
      refresh_token: refreshToken,
    },
    { attempts: 1 }
  );

/**
 * Logout: revoke the refresh token, which revokes the whole login on the
 * server (the access token stops working too). Best effort — a failure is
 * recorded but never blocks the local logout.
 */
export const revokeOidcRefreshToken = async (refreshToken) => {
  if (!refreshToken) {
    return false;
  }

  const { revocation_endpoint: revocationUrl } = await loadOidcConfiguration();

  try {
    await postSignedOidcForm(
      revocationUrl,
      {
        token: refreshToken,
        token_type_hint: 'refresh_token',
        client_id: config.api.client.user,
      },
      { attempts: 1 }
    );
    return true;
  } catch (error) {
    // RFC 7009 answers 200 with an empty body — an empty response is not a
    // failure, only a non-2xx status or a network error is.
    if (error?.name === 'TokenResponseParseError') {
      return true;
    }
    Sentry.captureException(error, {
      level: 'warning',
      extra: {
        revocation_url: revocationUrl,
        response_status: error?.status,
        response_body: error?.response,
      },
      tags: {
        internal_system: 'authentication',
        token_provider: 'oidc',
        token_exchange: 'revocation',
      },
    });
    return false;
  }
};

export const isOidcInvalidGrantError = (error) =>
  error?.code === 'invalid_grant';

/**
 * A 4xx OAuth error means the provider rejected the grant itself (the token
 * is expired, rotated, revoked, or malformed). Anything else is transient —
 * including a nonce challenge that could not be answered, which says nothing
 * about the grant: the provider rejects the proof before it looks at it.
 */
export const isOidcGrantRejected = (error) =>
  Boolean(error?.code) &&
  error.code !== 'use_dpop_nonce' &&
  error.status >= 400 &&
  error.status < 500;
